import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { useLang } from "../LangContext";
import { useTenant } from "../TenantContext";
import { MapPin, Home, Layers, Calendar, Landmark, Zap, FileText, Move3d, ArrowLeft, Download, Send, ShieldCheck, ChevronRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Row = ({ k, v }) => (
    <div className="flex justify-between py-2 border-b border-stone-100 last:border-0">
        <span className="text-stone-500 text-sm">{k}</span>
        <span className="text-stone-900 font-medium text-sm text-right">{v || "—"}</span>
    </div>
);

export default function ImmobileDetailPage() {
    const { id } = useParams();
    const { lang } = useLang();
    const { slug } = useTenant();
    const [imm, setImm] = useState(null);
    const [tab, setTab] = useState("overview");
    const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
    const [sent, setSent] = useState(false);

    useEffect(() => {
        window.scrollTo({ top: 0 });
        axios.get(`${API}/immobili/${id}`, { params: { borgo: slug } }).then((r) => setImm(r.data)).catch(() => setImm({ notfound: true }));
    }, [id, slug]);

    if (!imm) return <div className="pt-32 min-h-screen text-center text-stone-500">Caricamento…</div>;
    if (imm.notfound) return <div className="pt-32 min-h-screen text-center text-stone-500" data-testid="not-found">Immobile non trovato</div>;

    const cover = imm.marketing?.foto_copertina || imm.marketing?.galleria?.[0] || "";
    const gallery = imm.marketing?.galleria || [];
    const abs = (u) => (u && u.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

    const submitInterest = async (e) => {
        e.preventDefault();
        try {
            await axios.post(`${API}/immobili/${id}/interest`, { ...form, immobile_id: id }, { params: { borgo: slug } });
            setSent(true);
            setForm({ name: "", email: "", phone: "", message: "" });
        } catch { setSent(false); }
    };

    const dtRaw = imm.digital_twin?.embed_url || imm.digital_twin?.url || "";
    let dtUrl = dtRaw;
    if (dtRaw && !dtRaw.startsWith("http") && imm.digital_twin?.provider === "matterport") {
        dtUrl = `https://my.matterport.com/show/?m=${encodeURIComponent(dtRaw)}&play=1`;
    }

    return (
        <div className="pt-24 min-h-screen bg-stone-50" data-testid="immobile-detail-page">
            {/* Hero */}
            <section className="relative h-[45vh] md:h-[55vh] overflow-hidden bg-emerald-900">
                {cover ? (
                    <img src={abs(cover)} alt={imm.anagrafica?.denominazione} className="w-full h-full object-cover opacity-90" />
                ) : (
                    <div className="w-full h-full flex items-center justify-center text-emerald-200">
                        <Home size={80} />
                    </div>
                )}
                <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.55) 100%)" }} />
                <div className="absolute top-6 left-6">
                    <Link to="/immobili" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/90 backdrop-blur text-stone-800 text-sm font-semibold hover:bg-white transition-colors" data-testid="back-immobili">
                        <ArrowLeft size={14} />
                        {lang === "it" ? "Tutti gli immobili" : "All properties"}
                    </Link>
                </div>
                <div className="absolute bottom-8 left-8 right-8 text-white">
                    <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-emerald-200">
                        <span>{imm.codice}</span>
                        <ChevronRight size={12} />
                        <span>{imm.classificazione?.tipologia}</span>
                    </div>
                    <h1 className="font-serif text-3xl md:text-5xl lg:text-6xl font-semibold mt-2 drop-shadow-lg">
                        {imm.anagrafica?.denominazione}
                    </h1>
                    <div className="flex items-center gap-2 text-white/85 mt-2">
                        <MapPin size={16} />
                        <span className="text-sm">{imm.anagrafica?.via_piazza}, {imm.anagrafica?.comune}</span>
                    </div>
                </div>
            </section>

            <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Left: content */}
                <div className="lg:col-span-2 space-y-8">
                    {/* Highlights */}
                    <div className="bg-white rounded-2xl border border-stone-200 p-6 grid grid-cols-2 md:grid-cols-4 gap-4">
                        {[
                            { icon: Layers, label: "MQ", value: imm.dimensioni?.mq_calpestabili || 0 },
                            { icon: Home, label: "Vani", value: imm.dimensioni?.vani || 0 },
                            { icon: ShieldCheck, label: "Bagni", value: imm.dimensioni?.bagni || 0 },
                            { icon: Calendar, label: "Anno", value: imm.dimensioni?.anno_costruzione || "—" },
                        ].map((s, i) => {
                            const Icon = s.icon;
                            return (
                                <div key={i} className="text-center">
                                    <Icon size={18} className="text-emerald-600 mx-auto mb-1" />
                                    <div className="font-serif text-2xl font-semibold text-stone-900">{s.value}</div>
                                    <div className="text-xs uppercase tracking-wider text-stone-500 font-semibold">{s.label}</div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Description */}
                    {imm.marketing?.descrizione_pubblica && (
                        <div className="bg-white rounded-2xl border border-stone-200 p-6 md:p-8">
                            <h2 className="font-serif text-2xl font-semibold text-stone-900 mb-3">Descrizione</h2>
                            <p className="text-stone-700 leading-relaxed whitespace-pre-wrap">{imm.marketing.descrizione_pubblica}</p>
                        </div>
                    )}

                    {/* Digital Twin */}
                    {dtUrl && (
                        <div className="bg-emerald-950 rounded-2xl overflow-hidden" data-testid="digital-twin-embed">
                            <div className="flex items-center gap-3 px-6 py-4 text-white bg-gradient-to-r from-emerald-800 to-emerald-700">
                                <Move3d size={22} />
                                <div>
                                    <div className="font-serif text-xl font-semibold">Gemello Digitale · Tour 3D</div>
                                    <div className="text-xs text-emerald-200 uppercase tracking-wider">Provider: {imm.digital_twin?.provider}</div>
                                </div>
                            </div>
                            <div className="aspect-video bg-black">
                                <iframe
                                    src={dtUrl}
                                    title="Digital Twin"
                                    className="w-full h-full"
                                    allow="fullscreen; xr-spatial-tracking; camera; microphone"
                                    allowFullScreen
                                    frameBorder="0"
                                />
                            </div>
                        </div>
                    )}

                    {/* Gallery */}
                    {gallery.length > 0 && (
                        <div className="bg-white rounded-2xl border border-stone-200 p-6 md:p-8">
                            <h2 className="font-serif text-2xl font-semibold text-stone-900 mb-4">Galleria</h2>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                {gallery.map((g, i) => (
                                    <a key={i} href={abs(g)} target="_blank" rel="noreferrer" className="aspect-[4/3] overflow-hidden rounded-xl bg-stone-100">
                                        <img src={abs(g)} alt={`gallery ${i}`} className="w-full h-full object-cover hover:scale-105 transition-transform" />
                                    </a>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Tabbed data */}
                    <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
                        <div className="flex flex-wrap border-b border-stone-200">
                            {[
                                { id: "class", label: "Classificazione", icon: FileText },
                                { id: "cat", label: "Dati Catastali", icon: Landmark },
                                { id: "ub", label: "Ubicazione", icon: MapPin },
                                { id: "cert", label: "Certificazioni", icon: ShieldCheck },
                                { id: "imp", label: "Impianti", icon: Zap },
                            ].map((t) => {
                                const Icon = t.icon;
                                const on = tab === t.id;
                                return (
                                    <button
                                        key={t.id}
                                        onClick={() => setTab(t.id)}
                                        className={`flex items-center gap-2 px-4 md:px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
                                            on ? "border-emerald-600 text-emerald-700 bg-emerald-50/40" : "border-transparent text-stone-600 hover:text-emerald-700"
                                        }`}
                                        data-testid={`detail-tab-${t.id}`}
                                    >
                                        <Icon size={14} />
                                        {t.label}
                                    </button>
                                );
                            })}
                        </div>
                        <div className="p-6 md:p-8">
                            {tab === "class" && (
                                <div>
                                    <Row k="Tipo bene" v={imm.classificazione?.tipo_bene} />
                                    <Row k="Tipologia" v={imm.classificazione?.tipologia} />
                                    <Row k="Destinazione d'uso" v={imm.classificazione?.destinazione_uso} />
                                </div>
                            )}
                            {tab === "cat" && (
                                <div>
                                    <div className="text-xs font-bold uppercase tracking-wider text-amber-700 mb-2">Catasto Fabbricati</div>
                                    <Row k="Sezione Urbana" v={imm.dati_catastali?.fabbricati?.sezione_urbana} />
                                    <Row k="Foglio" v={imm.dati_catastali?.fabbricati?.foglio} />
                                    <Row k="Particella" v={imm.dati_catastali?.fabbricati?.particella} />
                                    <Row k="Subalterno" v={imm.dati_catastali?.fabbricati?.subalterno} />
                                    <Row k="Categoria" v={imm.dati_catastali?.fabbricati?.categoria} />
                                    <div className="text-xs font-bold uppercase tracking-wider text-amber-700 mb-2 mt-6">Catasto Terreni</div>
                                    <Row k="Foglio" v={imm.dati_catastali?.terreni?.foglio} />
                                    <Row k="Particella" v={imm.dati_catastali?.terreni?.particella} />
                                </div>
                            )}
                            {tab === "ub" && (
                                <div>
                                    <Row k="Indirizzo completo" v={imm.ubicazione?.indirizzo_completo} />
                                    <Row k="Civico" v={imm.ubicazione?.civico} />
                                    <Row k="CAP" v={imm.ubicazione?.cap} />
                                    <Row k="Latitudine" v={imm.ubicazione?.latitudine} />
                                    <Row k="Longitudine" v={imm.ubicazione?.longitudine} />
                                    {imm.ubicazione?.latitudine && imm.ubicazione?.longitudine && (
                                        <a
                                            href={`https://maps.google.com/?q=${imm.ubicazione.latitudine},${imm.ubicazione.longitudine}`}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="inline-flex items-center gap-1 mt-4 text-emerald-700 font-semibold text-sm hover:text-emerald-800"
                                        >
                                            <MapPin size={14} /> Vedi su Google Maps
                                        </a>
                                    )}
                                </div>
                            )}
                            {tab === "cert" && (
                                <div>
                                    <Row k="Classe APE" v={imm.certificazioni?.ape_classe} />
                                    <Row k="IPE" v={imm.certificazioni?.ape_ipe} />
                                    <Row k="Agibilità" v={imm.certificazioni?.agibilita ? "Sì" : "No"} />
                                </div>
                            )}
                            {tab === "imp" && (
                                <div>
                                    <Row k="Elettrico" v={imm.impianti?.elettrico} />
                                    <Row k="Idraulico" v={imm.impianti?.idraulico} />
                                    <Row k="Riscaldamento" v={imm.impianti?.riscaldamento} />
                                    <Row k="Tipo Riscaldamento" v={imm.impianti?.tipo_riscaldamento} />
                                    <Row k="Fotovoltaico" v={imm.impianti?.fotovoltaico ? "Sì" : "No"} />
                                    <Row k="Fibra Ottica" v={imm.impianti?.fibra_ottica ? "Sì" : "No"} />
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Right: sidebar */}
                <aside className="lg:col-span-1 space-y-6">
                    <div className="bg-white rounded-2xl border border-stone-200 p-6 sticky top-28">
                        <div className="text-xs font-bold uppercase tracking-wider text-amber-700 mb-1">Prezzo</div>
                        <div className="font-serif text-3xl font-semibold text-emerald-700">{imm.marketing?.prezzo || "Su richiesta"}</div>
                        <div className="text-xs text-stone-500 mt-1">Rif: <span className="font-mono font-semibold text-stone-700">{imm.codice}</span></div>

                        <a
                            href={`${API}/immobili/${id}/pdf?borgo=${slug}`}
                            target="_blank"
                            rel="noreferrer"
                            className="mt-5 w-full inline-flex items-center justify-center gap-2 py-3 px-4 rounded-full bg-amber-600 text-white font-semibold hover:bg-amber-700 transition-colors"
                            data-testid="download-fascicolo-pdf"
                        >
                            <Download size={16} />
                            {lang === "it" ? "Scarica Fascicolo PDF" : "Download Dossier PDF"}
                        </a>

                        <form onSubmit={submitInterest} className="mt-6 space-y-3 border-t border-stone-100 pt-5" data-testid="interest-form">
                            <div className="font-serif text-lg font-semibold text-stone-900">Richiedi info</div>
                            <input required placeholder="Nome" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-elegant" data-testid="interest-name" />
                            <input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input-elegant" data-testid="interest-email" />
                            <input placeholder="Telefono" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="input-elegant" data-testid="interest-phone" />
                            <textarea placeholder="Messaggio" rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="input-elegant resize-none" data-testid="interest-message" />
                            <button type="submit" className="btn-primary w-full py-3 rounded-full font-semibold inline-flex items-center justify-center gap-2" data-testid="interest-submit">
                                <Send size={16} />
                                {lang === "it" ? "Invia richiesta" : "Send request"}
                            </button>
                            {sent && <div className="text-emerald-700 text-sm text-center" data-testid="interest-sent">✓ Richiesta inviata. Ti contatteremo presto.</div>}
                        </form>
                    </div>
                </aside>
            </div>
        </div>
    );
}
